import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/showcaseApi';

/**
 * hooks/useBizCatalogMap.ts — queue #657 §R657-2.
 *
 * TakeThisPanel ③블록("소스 경로 + 사용 예 biz")이 `node.usedByBiz`(biz feature 키 목록)를
 * 라벨(nameKo) + 라우트(url) 딥링크로 해석하기 위한 소형 훅. 신규 endpoint 0 —
 * 기존 `GET /api/eraf/biz/catalog`(_index 소유 · getting-started-guide 도 재사용 중)를
 * read-only 로 그대로 소비한다. cross-feature 결합(모듈 import)을 피하기 위해 응답에서
 * 필요한 필드(feature/nameKo/url)만 담는 feature 로컬 최소 타입을 별도 선언한다.
 */
interface BizCatalogItem {
  feature: string;
  nameKo: string;
  url: string;
}

interface BizCatalogResponse {
  biz: BizCatalogItem[];
}

export interface BizCatalogMapEntry {
  nameKo: string;
  url: string;
}

export interface UseBizCatalogMapResult {
  bizMap: Map<string, BizCatalogMapEntry>;
  isLoading: boolean;
  isError: boolean;
}

export function useBizCatalogMap(): UseBizCatalogMapResult {
  const { data, isLoading, isError } = useQuery<BizCatalogResponse>({
    queryKey: ['biz-catalog'],
    queryFn: () =>
      apiClient
        .get<unknown, { data: BizCatalogResponse }>('/biz/catalog')
        .then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });

  const bizMap = useMemo(() => {
    const map = new Map<string, BizCatalogMapEntry>();
    for (const item of data?.biz ?? []) {
      map.set(item.feature, { nameKo: item.nameKo, url: item.url });
    }
    return map;
  }, [data]);

  return { bizMap, isLoading, isError };
}
